import { useContext, useState } from "react";
import { AppContext } from "../../../../context/app";
import {
  Button,
  Label,
  RadioGroup,
  RadioGroupItem,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@pushpress/pushpress-ui";
import { ExportPeopleFilterType, PeopleType } from "../../types";
import downloadCSV from "../../../../utils/downloadCSV";
import { useExportPeopleList } from "../../api/hooks";
import { toast } from "react-toastify";
import { defaultToastOptions } from "../../../../utils/misc";

type ExportPeopleModalProps = {
  activeTab: PeopleType;
  rowsSelected: string[];
  filters: ExportPeopleFilterType;
  total: number;
  closeModal: () => void;
};

const EXPORT_TYPES = {
  SELECTED: "selected",
  ALL: "all",
} as const;

export default function ExportPeopleModal({
  activeTab,
  rowsSelected,
  filters,
  closeModal,
  total,
}: ExportPeopleModalProps) {
  const [exportType, setExportType] = useState<
    (typeof EXPORT_TYPES)[keyof typeof EXPORT_TYPES]
  >(rowsSelected.length > 0 ? EXPORT_TYPES.SELECTED : EXPORT_TYPES.ALL);
  const { refetch } = useExportPeopleList(
    activeTab,
    exportType === EXPORT_TYPES.SELECTED ? rowsSelected : undefined,
    exportType === EXPORT_TYPES.ALL ? filters : undefined,
  );

  const { client } = useContext(AppContext);

  const handleExport = async () => {
    await refetch({ throwOnError: true })
      .then((response) => {
        downloadCSV({
          data: response.data,
          headers: undefined,
          fileName: `${client.company}-${activeTab}.csv`,
        });
        closeModal();
      })
      .catch(() => {
        toast.error("Export failed", defaultToastOptions);
      });
  };

  return (
    <Dialog open={true} onOpenChange={closeModal}>
      <DialogContent
        className="sm:max-w-[425px] [&>button]:tw-hidden"
        onPointerDownOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
      >
        <DialogHeader>
          <DialogTitle>Export</DialogTitle>
        </DialogHeader>
        <div className="tw-grid tw-gap-4 tw-pt-4">
          {rowsSelected.length > 0 ? (
            <>
              <div className="tw-flex tw-items-center tw-space-x-4">
                <RadioGroup
                  className="tw-flex tw-gap-6"
                  value={exportType}
                  onValueChange={(
                    value: (typeof EXPORT_TYPES)[keyof typeof EXPORT_TYPES],
                  ) => setExportType(value)}
                >
                  <div className="tw-flex tw-items-center tw-gap-4">
                    <RadioGroupItem
                      value={EXPORT_TYPES.SELECTED}
                      id="selected"
                    />
                    <Label htmlFor="selected" className="tw-mb-0">
                      Selected records ({rowsSelected.length})
                    </Label>
                  </div>
                  <div className="tw-flex tw-items-center tw-mr-4 tw-gap-4">
                    <RadioGroupItem value={EXPORT_TYPES.ALL} id="all" />
                    <Label htmlFor="all" className="tw-mb-0">
                      All ({total}) records
                    </Label>
                  </div>
                </RadioGroup>
              </div>
            </>
          ) : (
            <span className="tw-text-md tw-text-neutral-800">
              This will export all ({total}) records.
            </span>
          )}
          <div className="tw-flex tw-justify-end tw-gap-3">
            <Button variant="outline" size="default" onClick={closeModal}>
              Cancel
            </Button>
            <Button variant="default" size="default" onClick={handleExport}>
              Export
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
